"use client";
import React, { useState, useEffect } from "react";

import { CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ArrowUpDown, Info, ChevronDown, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { CurrencySelector } from "./currencyDropdown2";
import { createAddress } from "../services/digitalWalletService";
import PaymentIdSelect from "./PayWith";
import PhoneInput from "react-phone-number-input/input";
import "react-phone-number-input/style.css";
import {
  getEightyEightPayQuote,
  getAddressBook,
} from "../services/digitalWalletService";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useNotification } from "../context/NotificationProvider";
import { useToast } from "../hooks/use-toast";
import { useTradeRate } from "../hooks/query/queries";
import { truncateDecimals } from "../utils/numberFormatter";

export default function PayWith({
  userId: propUserId,
  onSelect,
  onConfirm,
  selectedAsset,
  setSelectedAsset,
  setParentAmount,
  parentAmount,
  address,
  setAddress,
  parentCurrentAmount,
  setParentCurrentAmount,
  initialPayId,
  currencyAssetsOverride,
}) {
  const { toast } = useToast();
  const [isSavedAddressesOpen, setIsSavedAddressesOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [convertedValue, setConvertedValue] = useState("0.000");
  const [availableAmount, setAvailableAmount] = useState(null);
  const [paymentId, setPaymentId] = useState("PayID");
  const [addressDetails, setAddressDetails] = useState(address);
  const [newLastname, setNewLastname] = useState("");
  const [newPayId, setNewPayId] = useState("");

  useEffect(() => {
    if (initialPayId) {
      setAddressDetails(initialPayId);
      setPaymentId("PayID");
    }
  }, [initialPayId]);
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [isBaseInput, setIsBaseInput] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [addBook, setAddBook] = useState([]);
  const [rate, setRate] = useState(null);
  const [selectedBalance, setSelectedBalance] = useState(selectedAsset);
  const [emailAddressFlag, setEmailAddressFlag] = useState(false);
  const [payIdFlag, setPayIdFlag] = useState(false);
  const [phoneFlag, setPhoneFlag] = useState(false);
  const [currentAmount, setCurrentAmount] = useState(parentCurrentAmount);
  const [amountFlag, setAmountFlag] = useState(false);
  const [flag, setFlag] = useState(false);
  const [minAmountFlag, setMinAmountFlag] = useState(false);
  const [currencyError, setCurrencyError] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { userProfile, settings } = useAuth();
  const { showPersistentMessage } = useNotification();
  const defaultCurrency = settings?.preferences?.localCurrency || "ZAR";
  const pair =
    selectedBalance?.asset + "-" + defaultCurrency.toString().toLowerCase();
  const isPostTrade = false;
  const { data: tradeRate } = useTradeRate(
    selectedBalance?.assetId,
    "buy",
    pair,
    isPostTrade
  );

  useEffect(() => {
    if (!selectedBalance || pair === "zar-zar") return;
    if (!tradeRate) return;

    try {
      setRate(tradeRate);
    } catch (err) {
      showPersistentMessage(
        "error",
        "Trading rates are not available at the moment please try again later"
      );
    }
  }, [selectedBalance, tradeRate, pair, showPersistentMessage]);

  const newestAddress = {
    name: newName,
    lastname: newLastname,
    payId: newPayId,
  };

  const addAddress = async () => {
    try {
      const address = await createAddress(newestAddress);
      setAddresses(address);
      setIsSavedAddressesOpen(false);
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
    }
  };

  const handleBalanceAmount = (balance) => {
    setSelectedBalance(balance);
    setSelectedAsset(balance);
    setAvailableAmount(selectedBalance?.availableAmount);
  };

  useEffect(() => {
    if (selectedBalance) {
      setAvailableAmount(selectedBalance.availableAmount);
    }
  }, [selectedBalance]);

  const detailPlaceholder =
    paymentId === "Email"
      ? "Enter email address"
      : paymentId === "Cellphone"
        ? "Enter cellphone number"
        : "Enter Pay ID....";

  const dialogTitleText = "Saved Addresses";

  const addNewLabel = "Add New Address";

  useEffect(() => {
    const getBook = async () => {
      const book = await getAddressBook();

      setAddBook(book.data.data.addresses);
    };
    getBook();
  }, [addresses]);

  useEffect(() => {
    const number = parseFloat(currentAmount || 0);

    if (isNaN(number) || number === 0) {
      setConvertedValue(0);
      return;
    }

    if (!rate) {
      setConvertedValue(number);
      return;
    }

    if (isBaseInput) {
      const convertedValue = number / rate.rate;
      const asset = selectedBalance?.asset?.toLowerCase();
      const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";
      const formattedValue = isTwoDecimalCurrency
        ? parseFloat(convertedValue.toFixed(2))
        : convertedValue;
      setConvertedValue(formattedValue);
      setParentAmount(formattedValue);
    } else {
      const convertedValue = number * rate.rate;
      const formattedValue = parseFloat(convertedValue.toFixed(2));
      setConvertedValue(formattedValue);
      setParentAmount(number);
    }
  }, [currentAmount, isBaseInput, rate, setParentAmount, selectedBalance]);

  const maxPayAmount = () => {
    if (isBaseInput) {
      const rEquivalent = selectedBalance?.availableAmount * rate?.rate;
      setCurrentAmount(rEquivalent);
      setParentCurrentAmount(rEquivalent);
    } else {
      setCurrentAmount(selectedBalance?.availableAmount);
      setParentCurrentAmount(selectedBalance?.availableAmount);
    }
    setAmountFlag(false);
    setMinAmountFlag(false);
    setFlag(false);
  };

  const switchConversion = () => {
    setIsBaseInput((prev) => !prev);
    const currentAmountValue = currentAmount || 0;
    setCurrentAmount(convertedValue);
    setParentCurrentAmount(convertedValue);
    setConvertedValue(currentAmountValue);
  };

  const handleContinue = () => {
    let hasErrors = false;

    setCurrencyError("");
    setDetailsError("");
    setAmountFlag(false);
    setMinAmountFlag(false);
    setFlag(false);

    if (!selectedBalance?.asset) {
      setCurrencyError("Please select a currency");
      hasErrors = true;
    }

    if (paymentId === "PayID" && !addressDetails) {
      setDetailsError("Please enter a PayID");
      hasErrors = true;
    } else if (paymentId === "Email" && !newEmail) {
      setDetailsError("Please enter an email address");
      hasErrors = true;
    } else if (paymentId === "Cellphone" && !newPhone) {
      setDetailsError("Please enter a phone number");
      hasErrors = true;
    } else if (emailAddressFlag || payIdFlag || phoneFlag) {
      hasErrors = true;
    }

    const rawAmount =
      typeof currentAmount === "string"
        ? currentAmount.replace(/,/g, "")?.trim()
        : currentAmount;
    const numericAmount = parseFloat(rawAmount);

    const zarValue = isBaseInput
      ? numericAmount
      : numericAmount * (rate?.rate ?? 0);
    const MIN_AMOUNT_ZAR = 1;

    if (
      rawAmount === "" ||
      rawAmount === null ||
      isNaN(numericAmount) ||
      numericAmount <= 0
    ) {
      setAmountFlag(true);
      setMinAmountFlag(false);
      hasErrors = true;
    } else if (zarValue < MIN_AMOUNT_ZAR) {
      setMinAmountFlag(true);
      setAmountFlag(false);
      hasErrors = true;
    } else {
      const maxAvailable = isBaseInput
        ? availableAmount * rate?.rate
        : availableAmount;

      if (numericAmount > maxAvailable) {
        setFlag(true);
        setAmountFlag(false);
        setMinAmountFlag(false);
        hasErrors = true;
      } else {
        setFlag(false);
        setMinAmountFlag(false);
      }
    }

    if (hasErrors) {
      return;
    }

    pay();
  };
  const pay = async () => {
    setIsLoading(true);
    try {
      const numericInput = parseFloat(
        typeof currentAmount === "string"
          ? currentAmount.replace(/,/g, "").trim()
          : currentAmount
      );
      const cryptoAmountForApi =
        rate?.rate != null && isBaseInput
          ? numericInput / rate.rate
          : numericInput;
      const quotePayload = {
        amount: cryptoAmountForApi,
        assetId: selectedBalance?.assetId,
      };
      const result = await getEightyEightPayQuote(quotePayload);
      const payQuote = result.data.data;
      const asset = selectedBalance?.asset?.toUpperCase?.() ?? "";
      const isCryptoTwoDecimals =
        selectedBalance?.asset?.toLowerCase() === "usdc" ||
        selectedBalance?.asset?.toLowerCase() === "usdt";
      const displayAmount = isBaseInput
        ? `R ${Number(numericInput).toFixed(2)}`
        : `${truncateDecimals(Number(payQuote?.amount ?? cryptoAmountForApi), asset === "BTC" ? 6 : isCryptoTwoDecimals ? 2 : 6)} ${asset}`;
      const displayTotalAmount = isBaseInput
        ? `R ${Number(numericInput).toFixed(2)}`
        : payQuote?.formattedTotalAmount != null
          ? payQuote.formattedTotalAmount
          : `${truncateDecimals(Number(payQuote?.totalAmount ?? cryptoAmountForApi), asset === "BTC" ? 6 : isCryptoTwoDecimals ? 2 : 6)} ${asset}`;
      const payQuoteWithDisplay = {
        ...payQuote,
        displayAmount,
        displayTotalAmount,
        amountDenomination: isBaseInput ? "ZAR" : "crypto",
        amountSecondary:
          rate?.rate != null && isBaseInput
            ? `${truncateDecimals(cryptoAmountForApi, asset === "BTC" ? 6 : isCryptoTwoDecimals ? 2 : 6)} ${asset}`
            : `R ${Number(cryptoAmountForApi * (rate?.rate ?? 0)).toFixed(2)}`,
      };
      if (paymentId === "PayID") {
        const info = {
          payId: addressDetails,
          assetId: selectedBalance?.assetId,
          userId: userProfile?._id,
        };

        if (onConfirm) {
          onConfirm(info);
        }
      }
      if (paymentId === "Cellphone") {
        const info = {
          phone: newPhone,
          assetId: selectedBalance?.assetId,
          userId: userProfile?._id,
        };
        if (onConfirm) {
          onConfirm(info);
        }
      }

      if (paymentId === "Email") {
        const info = {
          email: newEmail,
          assetId: selectedBalance?.assetId,
          userId: userProfile?._id,
        };
        if (onConfirm) {
          onConfirm(info);
        }
      }

      if (onSelect) {
        onSelect(payQuoteWithDisplay);
      }
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleAmountChange = (e) => {
    const newAmount = e.target.value;

    if (newAmount !== "" && !/^\d*\.?\d*$/.test(newAmount)) {
      return;
    }

    setAmountFlag(false);
    setMinAmountFlag(false);
    setFlag(false);
    setCurrentAmount(newAmount);
    setParentCurrentAmount(newAmount);

    const numericAmount = parseFloat(newAmount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setAmountFlag(true);
      return;
    }

    const zarValue = isBaseInput
      ? numericAmount
      : numericAmount * (rate?.rate ?? 0);
    if (zarValue < 1) {
      setMinAmountFlag(true);
      setAmountFlag(false);
      return;
    }

    const maxAvailable = isBaseInput
      ? availableAmount * rate?.rate
      : availableAmount;

    if (numericAmount > maxAvailable) {
      setFlag(true);
      setAmountFlag(false);
      setMinAmountFlag(false);
      return;
    }

    setAmountFlag(false);
    setMinAmountFlag(false);
    setFlag(false);
  };
  const disableButton = false;
  const onAddressChange = (e) => {
    const payIdType = /^[A-Z]\d{6}$/;

    const emailType = /^[\w.-]+@[\w.-]+\.\w{2,}$/;
    const value = e.target.value;
    if (paymentId === "Email" && emailType.test(value)) {
      setEmailAddressFlag(false);
      setNewEmail(value);
      setAddress(value);
    }
    if (paymentId === "Email" && !emailType.test(value)) {
      setNewEmail(value);
      setEmailAddressFlag(true);
      setPayIdFlag(false);
    }
    if (paymentId === "PayID" && payIdType.test(value)) {
      setPayIdFlag(false);
      setAddressDetails(value);
      setAddress(value);
    }
    if (paymentId === "PayID" && !payIdType.test(value)) {
      setAddressDetails(value);
      setPayIdFlag(true);
      setEmailAddressFlag(false);
    }
  };
  const onPhoneChange = (phone) => {
    const cellphoneType = /^\+27\d{9}$/;

    if (cellphoneType.test(phone)) {
      setNewPhone(phone);
      setAddress(phone);
      setPhoneFlag(false);
    }
    if (!cellphoneType.test(phone)) {
      setNewPhone(phone);
      setPhoneFlag(true);
    }
  };
  return (
    <CardContent className="px-0">
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="lg:w-[207px] w-full">
          <label className="block text-[14px] text-[#6D6584] mb-3">
            Address type
          </label>
          <PaymentIdSelect
            selectedId={paymentId}
            onSelect={(id) => {
              setPaymentId(id);
            }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <label className="block text-[14px] text-[#6D6584] mb-3">
            Details
          </label>
          <div
            className={`bg-[#EBE9EF4D] rounded-2xl px-4 lg:px-6 h-[88px] flex items-center justify-between gap-2 ${
              emailAddressFlag || phoneFlag || payIdFlag || detailsError
                ? "border border-[#DA2F58]"
                : ""
            }`}
          >
            {paymentId === "Cellphone" ? (
              <div className="flex items-center min-w-0 flex-1">
                <span className="text-[#6D6584] text-[16px] lg:text-[20px] font-semibold mr-2 flex-shrink-0">
                  +27
                </span>
                <PhoneInput
                  placeholder={detailPlaceholder}
                  className={`outline-none bg-transparent text-[16px] lg:text-[20px] font-semibold min-w-0 flex-1 ${
                    phoneFlag || detailsError
                      ? "text-[#DA2F58]"
                      : "text-[#6D6584]"
                  }`}
                  country="ZA"
                  value={newPhone}
                  onChange={(phone) => {
                    onPhoneChange(phone);
                    setDetailsError("");
                  }}
                />
              </div>
            ) : (
              <input
                type="text"
                placeholder={detailPlaceholder}
                className={`flex-1 min-w-0 outline-none bg-transparent text-[16px] lg:text-[20px] font-semibold ${
                  emailAddressFlag || payIdFlag || detailsError
                    ? "text-[#DA2F58]"
                    : "text-[#6D6584]"
                }`}
                value={paymentId === "Email" ? newEmail : addressDetails}
                onChange={(e) => {
                  onAddressChange(e);
                  setDetailsError("");
                }}
              />
            )}

            <Dialog
              open={isSavedAddressesOpen}
              onOpenChange={setIsSavedAddressesOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full h-[40px] lg:h-[48px] px-3 lg:px-6 border-[#EBE9EF] text-[#33125F] font-semibold flex items-center gap-1 lg:gap-2 text-[12px] lg:text-[14px] flex-shrink-0"
                >
                  <span className="hidden sm:inline">Open</span> Contacts
                  <ChevronDown size={12} />
                </Button>
              </DialogTrigger>
              <DialogContent
                className="max-w-md rounded-lg"
                aria-describedby="saved-addresses-description"
              >
                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold">
                    {dialogTitleText}
                  </DialogTitle>
                </DialogHeader>
                <div id="saved-addresses-description" className="mt-4">
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search addresses..."
                      className="w-full border rounded-lg px-3 py-2"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto pr-2">
                    {paymentId !== "PayID" ? (
                      <p className="text-center text-gray-500 py-4">
                        No saved{" "}
                        {paymentId === "Email" ? "emails" : "phone numbers"}.
                        Please enter manually.
                      </p>
                    ) : addBook && addBook.length > 0 ? (
                      addBook.map((addr) => {
                        const displayName =
                          addr.name && addr.lastname
                            ? `${addr.name} ${addr.lastname}`
                            : addr.name || addr.lastname || addr.payId;

                        return (
                          <div
                            key={addr._id}
                            className="border p-3 rounded-lg mb-2 flex justify-between items-center"
                          >
                            <div>
                              <p className="font-medium text-gray-800">
                                {displayName}
                              </p>
                              {(addr.name || addr.lastname) && addr.payId && (
                                <p className="text-sm text-gray-600">
                                  {addr.payId}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              className="text-xs"
                              onClick={() => {
                                setAddressDetails(addr.payId);
                                setAddress(addr.payId);
                                setPayIdFlag(false);
                                setDetailsError("");
                                setIsSavedAddressesOpen(false);
                              }}
                            >
                              Select
                            </Button>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-center text-gray-500">
                        No saved{" "}
                        {paymentId === "PayID"
                          ? "addresses"
                          : paymentId === "Email"
                            ? "emails"
                            : "cellphones"}
                      </p>
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    {showAll ? (
                      <Button
                        variant="link"
                        className="text-xs text-rose-600"
                        onClick={() => setShowAll(false)}
                      >
                        Show less
                      </Button>
                    ) : (
                      <Button
                        variant="link"
                        className="text-xs text-rose-600"
                        onClick={() => setShowAll(true)}
                      >
                        See more
                      </Button>
                    )}
                  </div>
                  <div className="mt-6 border-t pt-4">
                    <p className="font-semibold text-gray-800 mb-2">
                      {addNewLabel}
                    </p>
                    {paymentId === "PayID" && (
                      <>
                        <input
                          type="text"
                          placeholder="Name"
                          className="w-full border rounded-lg px-3 py-2 mb-2"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Lastname"
                          className="w-full border rounded-lg px-3 py-2 mb-2"
                          value={newLastname}
                          onChange={(e) => setNewLastname(e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Enter Pay ID"
                          className="w-full border rounded-lg px-3 py-2 mb-2"
                          value={newPayId}
                          onChange={(e) => setNewPayId(e.target.value)}
                        />
                        <Button
                          onClick={addAddress}
                          className="w-full mt-2 bg-gradient-to-tr from-purple-500 to-rose-500 rounded-full"
                        >
                          Create Address
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {detailsError && (
            <p className="mt-2 text-[14px] text-[#DA2F58]">{detailsError}</p>
          )}
          {!detailsError && emailAddressFlag && (
            <p className="mt-2 text-[14px] text-[#DA2F58]">
              Please enter a valid email address
            </p>
          )}
          {!detailsError && payIdFlag && (
            <p className="mt-2 text-[14px] text-[#DA2F58]">
              Please enter a valid PayID
            </p>
          )}
          {!detailsError && phoneFlag && (
            <p className="mt-2 text-[14px] text-[#DA2F58]">
              Please enter a valid phone number
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="lg:w-[207px] w-full">
          <label className="block text-[14px] text-[#6D6584] mb-3">
            Currency
          </label>
          <CurrencySelector
            userId={propUserId}
            onSelect={(balance) => {
              handleBalanceAmount(balance);
              setCurrencyError("");
            }}
            isEightyeightPay={true}
            isSend={false}
            setSelectedAsset={setSelectedBalance}
            selectedAsset={selectedBalance}
            disableButton={disableButton}
            showAvailableBalance={false}
            showLabel={false}
            hasError={!!currencyError}
            assetsOverride={currencyAssetsOverride}
          />
          {currencyError && (
            <p className="mt-2 text-[14px] text-[#DA2F58]">{currencyError}</p>
          )}
        </div>

        <div className="flex-1">
          <label className="block text-[14px] text-[#6D6584] mb-3">
            Available balance
          </label>
          <div className="bg-[#EBE9EF4D] rounded-2xl px-6 h-[88px] flex items-center justify-between">
            {pair === "zar-zar" ? (
              <>
                <span className="text-[20px] font-semibold text-[#6D6584]">
                  R{" "}
                  {availableAmount
                    ? parseFloat(availableAmount).toFixed(2)
                    : "0.00"}
                </span>
                <span className="text-[16px] text-[#6D6584]">
                  ≈ R{" "}
                  {availableAmount
                    ? parseFloat(availableAmount).toFixed(2)
                    : "0.00"}
                </span>
              </>
            ) : (
              <>
                <span className="text-[20px] font-semibold text-[#6D6584]">
                  {(() => {
                    const asset = selectedBalance?.asset?.toLowerCase();
                    const isTwoDecimalCurrency =
                      asset === "usdc" || asset === "usdt";
                    const decimals = isTwoDecimalCurrency ? 2 : 6;
                    return availableAmount
                      ? parseFloat(availableAmount).toFixed(decimals)
                      : "0.00";
                  })()}
                </span>
                <span className="text-[16px] text-[#6D6584]">
                  ≈ R{" "}
                  {availableAmount && rate
                    ? (parseFloat(availableAmount) * rate.rate).toFixed(2)
                    : "0.00"}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-[14px] text-[#6D6584] mb-3">
          Enter amount
        </label>
        <div
          className={`relative flex items-center justify-between rounded-2xl px-6 h-[88px] border transition-colors ${
            flag || amountFlag || minAmountFlag
              ? "border-2 border-rose-500"
              : "border-[#CECBD7]"
          }`}
        >
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <span className="text-[24px] font-semibold text-[#33125F] flex-shrink-0">
              {isBaseInput ? "R" : selectedBalance?.asset?.toUpperCase() || "R"}
            </span>
            <input
              type="text"
              value={currentAmount}
              onChange={(e) => {
                handleAmountChange(e);
              }}
              className={`flex-1 min-w-0 text-[24px] font-semibold bg-transparent outline-none ${
                flag || amountFlag || minAmountFlag
                  ? "text-rose-500"
                  : "text-[#33125F]"
              }`}
              placeholder="0"
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[16px] text-[#6D6584]">
              ≈{" "}
              {selectedBalance?.asset && pair !== "zar-zar"
                ? isBaseInput
                  ? (() => {
                      const asset = selectedBalance?.asset?.toLowerCase();
                      const isTwoDecimalCurrency =
                        asset === "usdc" || asset === "usdt";
                      const decimals = isTwoDecimalCurrency ? 2 : 6;
                      return `${selectedBalance.asset.toUpperCase()} ${
                        convertedValue === " " ||
                        isNaN(parseFloat(String(convertedValue)))
                          ? "0.00"
                          : truncateDecimals(
                              parseFloat(String(convertedValue)),
                              decimals
                            )
                      }`;
                    })()
                  : `R ${
                      convertedValue === " " ||
                      isNaN(parseFloat(String(convertedValue)))
                        ? "0.00"
                        : truncateDecimals(
                            parseFloat(String(convertedValue)),
                            2
                          )
                    }`
                : `R ${
                    currentAmount === " " ||
                    isNaN(parseFloat(String(currentAmount)))
                      ? "0.00"
                      : truncateDecimals(parseFloat(String(currentAmount)), 2)
                  }`}
            </span>
            {pair !== "zar-zar" && selectedBalance?.asset && (
              <Button
                onClick={switchConversion}
                variant="outline"
                size="icon"
                className="rounded-full h-[40px] w-[40px] border-[#EBE9EF]"
              >
                <ArrowUpDown className="h-3 w-3" />
              </Button>
            )}
            <Button
              onClick={maxPayAmount}
              variant="outline"
              className="rounded-full h-[40px] px-4 border-[#EBE9EF] text-[#6D6584] font-semibold"
            >
              Max
            </Button>
          </div>
        </div>

        {flag && (
          <p className="mt-1 text-sm text-rose-500">
            This amount exceeds your available balance
          </p>
        )}
        {minAmountFlag && !flag && (
          <p className="mt-1 text-sm text-rose-500">
            Minimum amount should be R1
          </p>
        )}
        {amountFlag && !flag && !minAmountFlag && (
          <p className="mt-1 text-sm text-rose-500">Please enter an amount</p>
        )}
      </div>

      <div className="flex justify-center mb-6">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-2 bg-[#EFF1FC] rounded-[16px] px-6 h-[48px] w-fit cursor-help">
                <Info size={16} className="text-[#6375E6] flex-shrink-0" />
                <p className="text-[14px] text-[#6375E6]">
                  88Pay is an off-chain transaction between 80eight users only.
                </p>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[300px] text-center">
              <p>
                88Pay allows instant transfers between 80eight users without
                blockchain fees. Transactions are processed internally.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex justify-center">
        <Button
          onClick={handleContinue}
          disabled={isLoading}
          className="bg-[linear-gradient(70.19deg,#57249C_10.66%,#EB3366_113.47%)] text-white px-6 rounded-full font-semibold hover:opacity-90 transition-opacity w-[302px] h-[48px] disabled:opacity-70"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </div>
    </CardContent>
  );
}
